import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery } from "@tanstack/vue-query";
import axios from "axios";
import {
  SearchApi,
  type SearchApiV2SearchAllGetRequest,
  type SearchApiV2SearchCategoriesGetRequest,
  type SearchApiV2SearchEventsGetRequest,
  type SearchApiV2SearchGetRequest,
  type SearchApiV2SearchProductsGetRequest,
  type SearchApiV2SearchSuggestionsGetRequest,
} from "mws-ts-v2-sdk";

const getSearchApi = () => {
  return new SearchApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    axios,
  );
};

export const searchQueryKeys = createQueryKeys("search", {
  // Old search endpoint still used for the modal
  results: (params: MaybeRef<SearchApiV2SearchGetRequest>) => ({
    queryKey: [params],
    queryFn: async () => {
      const response = await getSearchApi().v2SearchGet(unref(params));

      return response.data;
    },
  }),
  all: (params: MaybeRef<SearchApiV2SearchAllGetRequest>) => ({
    queryKey: [params],
    queryFn: async () => {
      const response = await getSearchApi().v2SearchAllGet(unref(params));

      return response.data;
    },
  }),
  products: (
    params: MaybeRefOrGetter<SearchApiV2SearchProductsGetRequest>,
  ) => ({
    queryKey: [params],
    queryFn: async () => {
      const response = await getSearchApi().v2SearchProductsGet(
        toValue(params),
      );

      return response.data;
    },
  }),
  suggestions: (
    params: MaybeRefOrGetter<SearchApiV2SearchSuggestionsGetRequest>,
  ) => ({
    queryKey: [params],
    queryFn: async () => {
      const response = await getSearchApi().v2SearchSuggestionsGet(
        toValue(params),
      );

      return response.data;
    },
  }),
});

export const useInfiniteCategoriesSearchQuery = (
  params: MaybeRef<SearchApiV2SearchCategoriesGetRequest>,
  enabled: Ref<boolean>,
) => {
  return useInfiniteQuery({
    queryKey: ["search", "categories", "infiniteList", params],
    queryFn: async ({ pageParam }) => {
      const response = await getSearchApi().v2SearchCategoriesGet({
        ...unref(params),
        offset: pageParam,
      });

      return response.data;
    },
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (unref(params).pageSize || 25);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
  });
};

export const useInfiniteProductsSearchQuery = (
  params: MaybeRef<SearchApiV2SearchProductsGetRequest>,
  enabled: Ref<boolean>,
) => {
  return useInfiniteQuery({
    queryKey: ["search", "products", "infiniteList", params],
    queryFn: async ({ pageParam }) => {
      const response = await getSearchApi().v2SearchProductsGet({
        ...unref(params),
        pageSize: unref(params).pageSize || 20,
        offset: pageParam,
      });

      return response.data;
    },
    enabled,
    placeholderData: (data) => data,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (unref(params).pageSize || 20);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
  });
};

export const useInfiniteEventsSearchQuery = (
  params: MaybeRef<SearchApiV2SearchEventsGetRequest>,
  enabled: Ref<boolean>,
) => {
  return useInfiniteQuery({
    queryKey: ["search", "events", "infiniteList", params],
    queryFn: async ({ pageParam }) => {
      const response = await getSearchApi().v2SearchEventsGet({
        ...unref(params),
        offset: pageParam,
      });

      return response.data;
    },
    enabled,
    placeholderData: (data) => data,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (unref(params).pageSize || 25);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
  });
};
